import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfirm } from "../../../hooks/useConfirm";
import {
  getCasesByLawyerWithFilters,
  getCaseStats,
  deleteCase,
} from "@/shared/db";
import { CaseCard } from "../components/CaseCard";
import { CasesTable } from "../components/CasesTable";
import { CreateCaseModal } from "../components/CreateCaseModal";
import { EditCaseModal } from "../components/EditCaseModal";
import {
  MagnifyingGlassIcon,
  BriefcaseIcon,
  PlusCircleIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { Button, Input, Select } from "@/shared/ui";
import { ListPageSkeleton } from "../../../components/skeletons";
import type {
  CaseWithDetails,
  CaseStatus,
  ViewMode,
  CaseNote,
} from "../types/case.types";
// Type guard to check if a value is a valid CaseNote
function isCaseNote(note: unknown): note is CaseNote {
  return (
    typeof note === "object" &&
    note !== null &&
    "id" in note &&
    "content" in note &&
    "author_id" in note &&
    "author_name" in note &&
    "timestamp" in note &&
    typeof (note as CaseNote).id === "string" &&
    typeof (note as CaseNote).content === "string" &&
    typeof (note as CaseNote).author_id === "string" &&
    typeof (note as CaseNote).author_name === "string" &&
    typeof (note as CaseNote).timestamp === "string"
  );
}

export const CasesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { confirm, Modal } = useConfirm();

  const [cases, setCases] = useState<CaseWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<CaseStatus>("all");
  const [practiceAreaFilter, setPracticeAreaFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem("casesViewMode") as ViewMode) || "card";
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    active: 0,
    closed: 0,
    archived: 0,
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseWithDetails | null>(
    null,
  );

  useEffect(() => {
    if (user) {
      loadCases();
      loadStats();
    }
  }, [user, statusFilter, practiceAreaFilter, priorityFilter, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem("casesViewMode", viewMode);
  }, [viewMode]);

  // Open create modal when navigating from dashboard "Crear primer caso"
  useEffect(() => {
    const state = location.state as { openCreateModal?: boolean } | null;
    if (state?.openCreateModal) {
      setIsCreateModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const loadCases = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const data = await getCasesByLawyerWithFilters(user.id, {
        status: statusFilter !== "all" ? statusFilter : undefined,
        practice_area:
          practiceAreaFilter !== "all" ? practiceAreaFilter : undefined,
        priority: priorityFilter !== "all" ? priorityFilter : undefined,
        search: searchTerm || undefined,
      });

      // Transform data to match CaseWithDetails type, converting Json notes to CaseNote[]
      const transformedData: CaseWithDetails[] = (data || []).map(
        (case_: unknown) => {
          const caseData = case_ as Record<string, unknown>;
          return {
            ...caseData,
            description: caseData.description ?? undefined,
            notes: Array.isArray(caseData.notes)
              ? caseData.notes.filter(isCaseNote)
              : undefined,
          } as CaseWithDetails;
        },
      );

      setCases(transformedData);
    } catch (error) {
      console.error("Error loading cases:", error);
      toast.error(t("lawyer.cases.error"));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!user) return;

    try {
      const statsData = await getCaseStats(user.id);
      setStats({
        total: statsData.total,
        pending: statsData.pending,
        active: statsData.active,
        closed: statsData.closed,
        archived: statsData.archived,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleView = (caseId: string) => {
    navigate(`/dashboard/lawyer/cases/${caseId}`);
  };

  const handleEdit = (case_: CaseWithDetails) => {
    setSelectedCase(case_);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (caseId: string) => {
    await confirm({
      title: t("lawyer.cases.delete.title"),
      message: t("lawyer.cases.delete.message"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
      onConfirm: async () => {
        try {
          await deleteCase(caseId);
          toast.success(t("lawyer.cases.delete.success"));
          loadCases();
          loadStats();
        } catch (error) {
          console.error("Error deleting case:", error);
          toast.error(t("lawyer.cases.delete.error"));
        }
      },
    });
  };

  const practiceAreas = [
    { code: "civil", name: t("lawyer.cases.practiceAreas.civil") },
    { code: "penal", name: t("lawyer.cases.practiceAreas.penal") },
    {
      code: "laboral",
      name: t("lawyer.cases.practiceAreas.laboral"),
    },
    {
      code: "comercial",
      name: t("lawyer.cases.practiceAreas.comercial"),
    },
    { code: "fiscal", name: t("lawyer.cases.practiceAreas.fiscal") },
    {
      code: "administrativo",
      name: t("lawyer.cases.practiceAreas.administrativo"),
    },
  ];

  if (loading && cases.length === 0) {
    return (
      <ListPageSkeleton
        showStats={true}
        showFilters={true}
        showViewToggle={true}
        viewMode={viewMode}
        statsCount={5}
        className="bg-linear-to-br from-romance-50 via-white to-romance-100"
      />
    );
  }

  return (
    <div className="mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold text-tiber-900">
              {t("lawyer.cases.title")}
            </h1>
            <p className="mt-2 text-base text-tiber-700">
              {t("lawyer.cases.subtitle", "Manage and track your legal cases")}
            </p>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            variant="primary"
            size="md"
            fullWidth={false}
            leftIcon={<PlusCircleIcon className="w-5 h-5" />}
          >
            {t("lawyer.cases.newCase")}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-linear-to-br from-tiber-500 to-tiber-600 rounded-xl p-6 shadow-lg"
          >
            <p className="text-tiber-100 text-sm font-medium mb-1">
              {t("lawyer.cases.stats.total")}
            </p>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-linear-to-br from-yellow-500 to-amber-600 rounded-xl p-6 shadow-lg"
          >
            <p className="text-yellow-100 text-sm font-medium mb-1">
              {t("lawyer.cases.stats.pending")}
            </p>
            <p className="text-3xl font-bold text-white">{stats.pending}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-linear-to-br from-atlantis-500 to-atlantis-600 rounded-xl p-6 shadow-lg"
          >
            <p className="text-white/90 text-sm font-medium mb-1">
              {t("lawyer.cases.stats.active")}
            </p>
            <p className="text-3xl font-bold text-white">{stats.active}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-linear-to-br from-tiber-500 to-tiber-700 rounded-xl p-6 shadow-lg"
          >
            <p className="text-white/90 text-sm font-medium mb-1">
              {t("lawyer.cases.stats.closed")}
            </p>
            <p className="text-3xl font-bold text-white">{stats.closed}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-linear-to-br from-tiber-500 to-tiber-600 rounded-xl p-6 shadow-lg"
          >
            <p className="text-tiber-100 text-sm font-medium mb-1">
              {t("lawyer.cases.stats.archived")}
            </p>
            <p className="text-3xl font-bold text-white">{stats.archived}</p>
          </motion.div>
        </div>

        {/* Filters and View Toggle */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-tiber-600">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Input
                  type="text"
                  placeholder={t("lawyer.cases.search.placeholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <Select
                options={[
                  {
                    value: "all",
                    label: t("lawyer.cases.filters.status.all"),
                  },
                  {
                    value: "pending",
                    label: t("lawyer.cases.filters.status.pending", "Pending"),
                  },
                  {
                    value: "active",
                    label: t("lawyer.cases.filters.status.active"),
                  },
                ]}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as CaseStatus)}
              />
            </div>

            {/* Practice Area Filter */}
            <div>
              <Select
                options={practiceAreas.map((area) => ({
                  value: area.code,
                  label: area.name,
                }))}
                value={practiceAreaFilter}
                onChange={(e) => setPracticeAreaFilter(e.target.value)}
              />
            </div>

            {/* Priority Filter */}
            <div>
              <Select
                options={[
                  {
                    value: "all",
                    label: t(
                      "lawyer.cases.filters.priority.all",
                      "All Priorities",
                    ),
                  },
                  {
                    value: "urgent",
                    label: t("lawyer.cases.filters.priority.urgent", "Urgent"),
                  },
                  {
                    value: "high",
                    label: t("lawyer.cases.filters.priority.high"),
                  },
                  {
                    value: "medium",
                    label: t("lawyer.cases.filters.priority.medium", "Medium"),
                  },
                  {
                    value: "low",
                    label: t("lawyer.cases.filters.priority.low"),
                  },
                ]}
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
              />
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center justify-end gap-2 mt-4">
            <span className="text-sm font-bold text-tiber-600 mr-2">
              {t("lawyer.cases.viewMode.label")}
            </span>
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "card"
                  ? "bg-atlantis-500 text-white"
                  : "bg-tiber-100 text-tiber-600 hover:bg-tiber-200"
              }`}
              title={t("lawyer.cases.viewMode.card")}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "table"
                  ? "bg-atlantis-500 text-white"
                  : "bg-tiber-100 text-tiber-600 hover:bg-tiber-200"
              }`}
              title={t("lawyer.cases.viewMode.table")}
            >
              <TableCellsIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Cases List */}
      {cases.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-lg">
          <BriefcaseIcon className="h-16 w-16 text-titan mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-juridico mb-2">
            {t("lawyer.cases.empty.title")}
          </h3>
          <p className="text-tiber-700 mb-6">
            {searchTerm ||
            statusFilter !== "all" ||
            practiceAreaFilter !== "all" ||
            priorityFilter !== "all"
              ? t(
                  "lawyer.cases.empty.filteredDescription",
                  "Try adjusting the search filters",
                )
              : t(
                  "lawyer.cases.empty.description",
                  "You don't have any cases yet. Create your first case to get started.",
                )}
          </p>
          {!searchTerm &&
            statusFilter === "all" &&
            practiceAreaFilter === "all" &&
            priorityFilter === "all" && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="px-6 py-3 bg-linear-to-r from-tiber-600 to-tiber-700 text-white rounded-lg hover:from-tiber-700 hover:to-tiber-800 transition-all shadow-lg hover:shadow-xl font-medium"
              >
                <PlusCircleIcon className="w-5 h-5 inline mr-2" />
                {t("lawyer.cases.createFirst")}
              </button>
            )}
        </div>
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((case_) => (
            <CaseCard
              key={case_.id}
              case={case_}
              onClick={() => handleView(case_.id)}
            />
          ))}
        </div>
      ) : (
        <CasesTable
          cases={cases}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />
      )}

      {/* Modals */}
      <CreateCaseModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          loadCases();
          loadStats();
        }}
      />

      <EditCaseModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCase(null);
        }}
        onSuccess={() => {
          loadCases();
          loadStats();
        }}
        case_={selectedCase}
      />

      {Modal}
    </div>
  );
};
